import React from 'react';
import {
    Box,
    Checkbox,
    CircularProgress,
    Divider,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import AudioFileOutlinedIcon from '@mui/icons-material/AudioFileOutlined';
import VideoFileOutlinedIcon from '@mui/icons-material/VideoFileOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {fetchSourceRawUrl} from './sourcesApi';
import {resolveFileProcessingStatus} from './sourcesFileMetadata';
import SourcesFileInfoDialog from './SourcesFileInfoDialog';

function FileTypeIcon({fileType, fontSize = 'small'}) {
    if (fileType === 'pdf') return <PictureAsPdfOutlinedIcon fontSize={fontSize}/>;
    if (fileType === 'audio') return <AudioFileOutlinedIcon fontSize={fontSize}/>;
    if (fileType === 'video') return <VideoFileOutlinedIcon fontSize={fontSize}/>;
    if (fileType === 'image') return <ImageOutlinedIcon fontSize={fontSize}/>;
    if (fileType === 'html') return <LanguageOutlinedIcon fontSize={fontSize}/>;
    if (fileType === 'text') return <TextSnippetOutlinedIcon fontSize={fontSize}/>;
    // document / docx / unknown
    return <DescriptionOutlinedIcon fontSize={fontSize}/>;
}

function FileStatusLine({status, isActive}) {
    if (!status) {
        return null;
    }

    const color = (() => {
        if (status.kind === 'failed') {
            return isActive ? 'error.light' : 'error.main';
        }
        if (status.kind === 'ready') {
            return isActive ? 'primary.contrastText' : 'success.main';
        }
        return isActive ? 'primary.contrastText' : 'warning.main';
    })();

    const content = (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
                maxWidth: '100%',
            }}
        >
            {status.showSpinner && (
                <CircularProgress
                    size={10}
                    thickness={6}
                    sx={{color, flexShrink: 0}}
                />
            )}
            {status.kind === 'ready' && (
                <CheckCircleOutlineIcon sx={{fontSize: 12, color, flexShrink: 0, opacity: isActive ? 0.85 : 1}}/>
            )}
            {status.kind === 'failed' && (
                <ErrorOutlineIcon sx={{fontSize: 12, color, flexShrink: 0}}/>
            )}
            <Typography
                variant="caption"
                component="span"
                noWrap
                sx={{
                    color,
                    fontSize: '0.6875rem',
                    lineHeight: 1.2,
                    opacity: status.kind === 'ready' && isActive ? 0.85 : 1,
                }}
            >
                {status.label}
            </Typography>
        </Box>
    );

    if (status.errorMessage) {
        return (
            <Tooltip title={status.errorMessage} arrow placement="bottom-start">
                {content}
            </Tooltip>
        );
    }

    if (status.kind === 'ready') {
        return (
            <Tooltip title="Extracted and ready" arrow placement="bottom-start">
                {content}
            </Tooltip>
        );
    }

    return content;
}

const SourcesFileListItem = ({
    file,
    isSelected,
    onClick,
    onRename,
    onShare,
    onDelete,
    onMove,
    userId,
    draggable = false,
    onDragStart,
    selectionEnabled = false,
    isSelectionChecked = false,
    onSelectionToggle,
}) => {
    const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
    const [isHovered, setIsHovered] = React.useState(false);
    const [isInfoOpen, setIsInfoOpen] = React.useState(false);

    const openMenu = React.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    }, []);

    const closeMenu = React.useCallback((event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        setMenuAnchorEl(null);
    }, []);

    const handleShowInfo = React.useCallback((event) => {
        closeMenu(event);
        setIsInfoOpen(true);
    }, [closeMenu]);

    const handleOpen = React.useCallback((event) => {
        closeMenu(event);
        onClick?.(event);
    }, [closeMenu, onClick]);

    const handleAction = React.useCallback((action) => (event) => {
        closeMenu(event);
        action?.(file);
    }, [closeMenu, file]);

    const handleDownloadRaw = React.useCallback(async (event) => {
        closeMenu(event);
        if (!userId || !file?.id) {
            return;
        }
        try {
            const payload = await fetchSourceRawUrl({referenceId: file.id, userId});
            if (payload?.url) {
                window.open(payload.url, '_blank', 'noopener,noreferrer');
            }
        } catch (err) {
            console.error('[SourcesFileListItem] Raw download failed:', err);
        }
    }, [closeMenu, file?.id, userId]);

    const isBulkSelected = Boolean(isSelectionChecked);
    const isActive = Boolean(isSelected);
    const processingStatus = resolveFileProcessingStatus(file);

    return (
        <React.Fragment>
            <ListItem
                disablePadding
                data-selected={isSelected ? 'true' : 'false'}
                draggable={draggable}
                onDragStart={(event) => onDragStart?.(event, file)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                sx={(theme) => ({
                    mb: 1,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    bgcolor: isActive
                        ? theme.palette.primary.main
                        : (isBulkSelected ? theme.palette.action.selected : theme.palette.background.paper),
                    border: '1px solid',
                    borderColor: isActive
                        ? theme.palette.primary.main
                        : (isBulkSelected ? theme.palette.secondary.main : theme.palette.divider),
                    boxShadow: isActive ? theme.shadows[3] : 'none',
                    outline: isBulkSelected ? `2px solid ${theme.palette.secondary.main}` : 'none',
                    outlineOffset: '-2px',
                    transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'outline-color']),
                })}
            >
                <ListItemButton
                    onClick={onClick}
                    selected={isSelected}
                    aria-current={isSelected ? 'true' : undefined}
                    sx={(theme) => ({
                        alignItems: 'flex-start',
                        borderRadius: 0,
                        px: 1.5,
                        py: 1,
                        gap: 0.75,
                        transition: theme.transitions.create(['background-color', 'box-shadow', 'color']),
                        bgcolor: isActive
                            ? theme.palette.primary.main
                            : (isBulkSelected ? theme.palette.action.selected : theme.palette.background.paper),
                        color: isActive ? theme.palette.primary.contrastText : theme.palette.text.primary,
                        '& .MuiListItemIcon-root': {
                            color: isActive ? theme.palette.primary.contrastText : theme.palette.text.secondary,
                        },
                        '& .file-actions-button': {
                            color: isActive ? theme.palette.primary.contrastText : theme.palette.text.secondary,
                        },
                        '&.Mui-selected': {
                            bgcolor: theme.palette.primary.main,
                            '& .MuiListItemIcon-root': {
                                color: theme.palette.primary.contrastText,
                            },
                            '& .file-actions-button': {
                                color: theme.palette.primary.contrastText,
                            },
                        },
                        '&:hover': {
                            bgcolor: isActive ? theme.palette.primary.dark : theme.palette.action.hover,
                        },
                        '&.Mui-selected:hover': {
                            bgcolor: theme.palette.primary.dark,
                        },
                    })}
                >
                    <ListItemIcon sx={{minWidth: 34, mt: 0.125}}>
                        <Box
                            sx={{
                                width: 20,
                                height: 20,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {selectionEnabled && (isHovered || isSelectionChecked) ? (
                                <Checkbox
                                    size="small"
                                    edge="start"
                                    checked={isSelectionChecked}
                                    sx={{p: 0, m: 0, width: 20, height: 20}}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        onSelectionToggle?.(event, file);
                                    }}
                                />
                            ) : (
                                <FileTypeIcon fileType={file.fileType} fontSize="small"/>
                            )}
                        </Box>
                    </ListItemIcon>
                    <Box sx={{flex: 1, minWidth: 0, py: 0.125}}>
                        <Typography
                            variant="body2"
                            component="div"
                            title={file.name}
                            sx={{
                                fontSize: '0.8125rem',
                                fontWeight: isActive ? 600 : 500,
                                lineHeight: 1.35,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                wordBreak: 'break-word',
                                color: 'inherit',
                            }}
                        >
                            {file.name}
                        </Typography>
                        {(processingStatus || file.source || file.sharedByUserId || (file.cmStatus && file.cmCreatorName)) && (
                            <Box
                                sx={{
                                    mt: 0.375,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: 0.25,
                                    minWidth: 0,
                                    maxWidth: '100%',
                                }}
                            >
                                <FileStatusLine status={processingStatus} isActive={isActive}/>
                                {(file.cmStatus && file.cmCreatorName) && (
                                    <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{
                                            fontSize: '0.6875rem',
                                            color: isActive ? 'primary.contrastText' : 'text.secondary',
                                            opacity: isActive ? 0.8 : 1,
                                            maxWidth: '100%',
                                        }}
                                    >
                                        {`Collected by: ${file.cmCreatorName}`}
                                    </Typography>
                                )}
                                {file.sharedByUserId && (
                                    <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{
                                            fontSize: '0.6875rem',
                                            color: isActive ? 'primary.contrastText' : 'info.main',
                                            opacity: isActive ? 0.85 : 1,
                                            maxWidth: '100%',
                                        }}
                                    >
                                        {file.sharedBy?.displayName
                                            ? `Shared by ${file.sharedBy.displayName}`
                                            : 'Shared with you'}
                                    </Typography>
                                )}
                                {file.source && (
                                    <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{
                                            fontSize: '0.6875rem',
                                            color: isActive ? 'primary.contrastText' : 'text.secondary',
                                            opacity: isActive ? 0.8 : 1,
                                            maxWidth: '100%',
                                        }}
                                    >
                                        {file.source}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                    <IconButton
                        aria-label="file actions"
                        edge="end"
                        size="small"
                        className="file-actions-button"
                        onClick={openMenu}
                        sx={{mt: -0.25, mr: -0.5, flexShrink: 0}}
                    >
                        <MoreVertIcon fontSize="small"/>
                    </IconButton>
                </ListItemButton>
            </ListItem>
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={closeMenu}
            >
                {onClick && <MenuItem onClick={handleOpen}>Open</MenuItem>}
                <MenuItem onClick={handleShowInfo}>Get Info</MenuItem>
                <MenuItem onClick={handleDownloadRaw}>Download</MenuItem>
                {onShare && !file.sharedByUserId && (
                    <MenuItem onClick={handleAction(onShare)}>Share</MenuItem>
                )}
                <MenuItem onClick={handleAction(onRename)}>Rename</MenuItem>
                {onMove && <MenuItem onClick={handleAction(onMove)}>Move to folder...</MenuItem>}
                <Divider/>
                <MenuItem onClick={handleAction(onDelete)}>Delete</MenuItem>
            </Menu>
            <SourcesFileInfoDialog
                open={isInfoOpen}
                onClose={() => setIsInfoOpen(false)}
                file={file}
                userId={userId}
            />
        </React.Fragment>
    );
};

export default React.memo(SourcesFileListItem);
